import type { Ref } from 'react'

import { TEDDIES, teddyPhotoPath, type Peg } from '@hazel/shared'

import { fallBackToDrawing } from '../components/teddyPicture.js'

export interface MastermindPegProps {
  readonly at: number
  readonly peg: Peg | null
  /** Where the keyboard is pointing, if it is pointing here. */
  readonly cursor: boolean
  readonly pegClassName: string
  readonly ref?: Ref<HTMLButtonElement>
  readonly onPick: (element: HTMLButtonElement | null) => void
}

/**
 * One place in the row she is building.
 *
 * Always a button, so every place is reachable and announced rather than being
 * a silent div — and so a finger works as well as a keyboard, the same rule
 * Stripey's and Maritza's squares follow.
 *
 * Named for the teddy in it and where it is — "place 2, Nemo" — because the
 * index is an engine detail. An empty place says so rather than being silent,
 * or a screen reader walking the row would skip the holes, which are the part
 * she still has to fill.
 */
export function MastermindPeg({ at, peg, cursor, pegClassName, ref, onPick }: MastermindPegProps) {
  const name = peg ? TEDDIES[peg].name : 'empty'

  return (
    <button
      type="button"
      ref={ref}
      // One tab stop for the whole row. Tab lands where the cursor already is.
      tabIndex={cursor ? 0 : -1}
      aria-label={`Place ${at + 1}, ${name}`}
      onClick={(event) => {
        onPick(event.currentTarget)
      }}
      style={{ touchAction: 'manipulation' }}
      className={[
        'relative flex items-center justify-center overflow-hidden rounded-full',
        'border-border bg-surface border-2 p-0 transition-colors',
        pegClassName,
        cursor ? 'ring-focus z-10 ring-4' : '',
      ].join(' ')}
    >
      {peg ? (
        <img
          src={teddyPhotoPath(peg)}
          alt=""
          className="size-full object-cover"
          onError={(event) => fallBackToDrawing(event.currentTarget, peg)}
        />
      ) : null}
    </button>
  )
}

export interface PlayedPegProps {
  readonly peg: Peg
  readonly pegClassName: string
}

/**
 * One teddy in a row she has already played.
 *
 * Not a button: there is nothing to press. A guess that has been spent cannot
 * be changed, and a row of controls that do nothing is a row of promises the
 * screen does not keep — which on a television full of them is worse than it
 * sounds, because every one is a tab stop between her and the board.
 */
export function PlayedPeg({ peg, pegClassName }: PlayedPegProps) {
  return (
    <span
      className={[
        'border-border bg-surface flex items-center justify-center',
        'overflow-hidden rounded-full border-2',
        pegClassName,
      ].join(' ')}
    >
      <img
        src={teddyPhotoPath(peg)}
        alt={TEDDIES[peg].name}
        className="size-full object-cover"
        onError={(event) => fallBackToDrawing(event.currentTarget, peg)}
      />
    </span>
  )
}
